import React from 'react';
import { IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u3_x5_termische';

const TEXTS = {
  intro:
    'Thermisch auslosende Brandschutzklappen (TBSK) sind wichtige passive Brandschutzvorrichtungen in Luftungsanlagen. Sie verhindern im Brandfall die Ausbreitung von Feuer und Rauch uber die Luftungskanale in andere Brandabschnitte. Im Gegensatz zu motorisch betriebenen Klappen werden TBSK durch die Hitze des Brandes ausgelost.',
  mechanism:
    'TBSK funktionieren mechanisch und ohne externe Stromversorgung. Kernstuck ist ein Schmelzlotaufsatz, der bei Erreichen einer bestimmten Temperatur (meist 72C oder 95C) schmilzt. Dadurch wird eine Federkraft freigesetzt, die die Klappe schliesst. Die Klappen sind in der Regel in Ruhestellung offen.',
  application:
    'TBSK eignen sich besonders fur kleinere bis mittlere Luftungsanlagen, bei denen keine standige Uberwachung der Klappenposition erforderlich ist. Sie werden oft in Burogebauden, Wohnanlagen, Hotels und anderen Gebauden eingesetzt, in denen eine einfache und zuverlassige Brandschutzlosung benotigt wird.',
  manufacturerIntro: 'Zu den bekannten Herstellern von TBSK gehoren:',
  manufacturers: [
    { label: 'Schako', text: 'Breites Sortiment an Brandschutzklappen.' },
    { label: 'Rauchschutz', text: 'Spezialist fur Rauch- und Brandschutz.' },
    { label: 'TROX', text: 'Grosses Portfolio an Brandschutzlosungen.' }
  ],
  manufacturersFooter:
    'Diese Hersteller bieten verschiedene Modelle und Ausfuhrungen fur unterschiedliche Anforderungen und Luftungssysteme an.',
  maintenance:
    'TBSK mussen regelmassig auf ihre Funktionstuchtigkeit gepruft werden. Dazu gehort die Kontrolle der Schmelzsicherung, der Federkraft und der Klappenbewegung. Bei Bedarf mussen die Klappen gewartet oder ausgetauscht werden. Die Wartung wird meist in der Wartung der Luftungsanlage mit gepruft.',
  gltIntro:
    'Obwohl TBSK rein mechanisch funktionieren, ist in vielen Fallen eine Anbindung an die Gebaudeleittechnik (GLT) sinnvoll. Dies erfolgt uber eine Endlagenuberwachung. An der Klappe ist ein Melder angebracht, der entweder meldet, dass die Klappe offen ist, oder dass sie geschlossen ist. Diese Melder arbeiten mit einem Ruhestromprinzip und sind somit Drahtbruchsicher. Der Zustand der Klappe wird uber einen Kontakt (potentialfrei) in die GLT ubertragen.',
  gltMonitoring:
    'Die Uberwachung des Kontaktes in der GLT erfolgt auf Drahtbruch. Dies bedeutet, dass sowohl eine offene als auch eine geschlossene Klappe als aktiver Zustand erkannt werden konnen. Sollte es zu einem Drahtbruch kommen, wird dies als Storung gemeldet. So ist sichergestellt, dass bei einer Storung, die Luftungsanlage ausser Betrieb geht.',
  gltResponse:
    'Die Anbindung an die GLT ermoglicht die Uberwachung des Klappenstatus. Im Brandfall, ausgelost durch die Brandmeldeanlage (BMA), wird ein Signal an die GLT gesendet. Abhangig von der programmierten Brandfallmatrix schaltet die GLT dann die betroffene Luftungsanlage oder alle Luftungsanlagen ab. Die GLT kann dabei in ihrer Matrix differenzieren welche Luftungsanlagen abgeschalten werden mussen, oder ob es fur den Brandabschnitt relevant ist.'
};

const bulletToText = (items: Array<{ label: string; text: string }>) => items.map((i) => `${i.label}: ${i.text}`);

const textBlocks = [
  { id: 'block_intro', heading: 'Einfuhrung', text: TEXTS.intro },
  { id: 'block_mechanismus', heading: 'Mechanismus', text: TEXTS.mechanism },
  { id: 'block_einsatz', heading: 'Einsatzgebiete', text: TEXTS.application },
  {
    id: 'block_hersteller',
    heading: 'Hersteller',
    text: TEXTS.manufacturerIntro + ' ' + bulletToText(TEXTS.manufacturers).join(' ') + ' ' + TEXTS.manufacturersFooter
  },
  { id: 'block_wartung', heading: 'Wartung', text: TEXTS.maintenance },
  {
    id: 'block_glt',
    heading: 'GLT Anbindung',
    text: TEXTS.gltIntro + ' ' + TEXTS.gltMonitoring + ' ' + TEXTS.gltResponse
  }
];

const Page: React.FC = () => {
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const canEditTexts = canEditTextsM1;
  const canUploadImages = canUploadImagesM1;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map((b) => ({
      ...b,
      text: override?.texts?.[b.id] ?? b.text
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map((b) => `${b.heading}. ${b.text}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleImageReplace = React.useCallback(
    async (key: string, file: File) => {
      if (!token) return alert('Kein Token.');
      if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
      try {
        await uploadUnitAsset(PAGE_ID, key, file, token);
        alert('Bild aktualisiert.');
        refresh();
      } catch (e: any) {
        alert('Upload fehlgeschlagen: ' + (e?.message || e));
      }
    },
    [token, refresh, canUploadImages]
  );

  const handleTextSave = React.useCallback(
    async (blockId: string, body: string) => {
      if (!token) return alert('Kein Token.');
      if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
      try {
        await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
        alert('Text gespeichert.');
        refresh();
      } catch (e: any) {
        alert('Speichern fehlgeschlagen: ' + (e?.message || e));
      }
    },
    [token, refresh, canEditTexts]
  );

  const actions = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '1.3.5 Thermische BSKs', actions },
    React.createElement(
      Box,
      { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bilder links
      React.createElement(
        Box,
        { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(ZoomableImage, {
          src: imgSrc('img_0', '/assets/drawable/bsk_thermisch.jpeg'),
          alt: 'Thermische Brandschutzklappe',
          label: 'Bild einfugen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_0', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_1', '/assets/drawable/bsk_termisch02.webp'),
          alt: 'Thermische BSK Detail',
          label: 'Bild einfugen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_1', f) : undefined
        }),
        React.createElement(ZoomableImage, {
          src: imgSrc('img_2', '/assets/drawable/bsk_warten.jpeg'),
          alt: 'Wartung Thermische BSK',
          label: 'Bild einfugen',
          onReplace: canUploadImages ? (f: File) => handleImageReplace('img_2', f) : undefined
        })
      ),
      // Texte rechts
      React.createElement(
        Box,
        { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map((b) =>
          React.createElement(EditableTextBlock, {
            key: b.id,
            pageId: PAGE_ID,
            blockId: b.id,
            heading: b.heading,
            body: b.text,
            onSave: (body: string) => handleTextSave(b.id, body)
          })
        )
      )
    )
  );
};

export default Page;
